import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    IconButton,
    InputAdornment,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { Close, FilterAltOutlined, Search } from "@mui/icons-material";
import i18next from "i18next";
import { fetchCustomerPtfVersions } from "../../api/api";

interface CustomerPtfVersion {
    customer_number: string;
    customer_name: string;
    product_full_name: string;
    product_code: string;
    environment_type: string;
    version_major: string;
    applied_version: string;
}

type CustomerPtfVersionColumn = keyof CustomerPtfVersion;

const customerPtfVersionColumns: Array<{ key: CustomerPtfVersionColumn; labelKey: string }> = [
    { key: "customer_number", labelKey: "customerPtfVersions.customerNumber" },
    { key: "customer_name", labelKey: "customerPtfVersions.customer" },
    { key: "product_full_name", labelKey: "customerPtfVersions.product" },
    { key: "product_code", labelKey: "customerPtfVersions.productCode" },
    { key: "environment_type", labelKey: "customerPtfVersions.environment" },
    { key: "version_major", labelKey: "customerPtfVersions.versionMajor" },
    { key: "applied_version", labelKey: "customerPtfVersions.appliedVersion" }
];

const Spinner = () => (
    <div className="full-page-spinner">
        <div className="spinner"></div>
    </div>
);

const CustomerPtfVersions = () => {
    const [versions, setVersions] = useState<CustomerPtfVersion[]>([]);
    const [filter, setFilter] = useState("");
    const [columnFilters, setColumnFilters] = useState<Record<CustomerPtfVersionColumn, string>>({
        customer_number: "",
        customer_name: "",
        product_full_name: "",
        product_code: "",
        environment_type: "",
        version_major: "",
        applied_version: ""
    });
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [activeFilterColumn, setActiveFilterColumn] = useState<CustomerPtfVersionColumn | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    const loadVersions = async () => {
        setIsLoading(true);
        setLoadError("");
        try {
            const response = await fetchCustomerPtfVersions();
            const result = await response.json();
            if (!response.ok || result?.error) {
                setLoadError(result?.error || i18next.t("customerPtfVersions.loadError"));
                return;
            }
            setVersions(Array.isArray(result?.customer_ptf_versions) ? result.customer_ptf_versions : []);
        } catch (error) {
            console.error("Error loading customer PTF versions:", error);
            setLoadError(i18next.t("customerPtfVersions.loadError"));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadVersions();
    }, []);

    const filteredVersions = useMemo(() => {
        const normalizedFilter = filter.trim().toLocaleLowerCase();
        return versions.filter(
            version =>
                (!normalizedFilter ||
                    [
                        version.customer_number,
                        version.customer_name,
                        version.product_full_name,
                        version.product_code,
                        version.environment_type,
                        version.version_major,
                        version.applied_version
                    ].some(value =>
                        String(value || "")
                            .toLocaleLowerCase()
                            .includes(normalizedFilter)
                    )) &&
                customerPtfVersionColumns.every(({ key }) => {
                    const normalizedColumnFilter = columnFilters[key].trim().toLocaleLowerCase();
                    return (
                        !normalizedColumnFilter ||
                        String(version[key] || "")
                            .toLocaleLowerCase()
                            .includes(normalizedColumnFilter)
                    );
                })
        );
    }, [columnFilters, filter, versions]);

    const openColumnFilter = (event: React.MouseEvent<HTMLElement>, column: CustomerPtfVersionColumn) => {
        setFilterAnchor(event.currentTarget);
        setActiveFilterColumn(column);
    };

    const closeColumnFilter = () => {
        setFilterAnchor(null);
        setActiveFilterColumn(null);
    };

    return (
        <div>
            {isLoading && <Spinner />}
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    padding: "13px",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "13px",
                    flexWrap: "wrap"
                }}
            >
                <h2 style={{ fontSize: "xx-large", fontWeight: "bold", fontFamily: "BIZ UDPGothic, sans-serif", margin: 0 }}>
                    {i18next.t("customerPtfVersions.pageTitle")}
                </h2>
                <TextField
                    label={i18next.t("customerPtfVersions.filter")}
                    value={filter}
                    onChange={event => setFilter(event.target.value)}
                    size="small"
                    sx={{ width: { xs: "100%", sm: 360 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
            </div>
            <Typography variant="body2" color="text.secondary" sx={{ px: "13px", pb: 2 }}>
                {i18next.t("customerPtfVersions.pageDescription")}
            </Typography>
            {loadError && (
                <Alert severity="error" sx={{ mx: "13px", mb: 2 }}>
                    {loadError}
                </Alert>
            )}
            <TableContainer component={Paper} sx={{ mx: "13px", width: "calc(100% - 26px)", maxHeight: "calc(100vh - 180px)", overflow: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1250 }}>
                    <TableHead>
                        <TableRow>
                            {customerPtfVersionColumns.map(({ key, labelKey }) => (
                                <TableCell key={key}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "2px", whiteSpace: "nowrap" }}>
                                        {i18next.t(labelKey)}
                                        <IconButton
                                            aria-label={`${i18next.t("customerPtfVersions.headerFilter")} ${i18next.t(labelKey)}`}
                                            title={`${i18next.t("customerPtfVersions.headerFilter")} ${i18next.t(labelKey)}`}
                                            onClick={event => openColumnFilter(event, key)}
                                            size="small"
                                            color={columnFilters[key] ? "primary" : "default"}
                                        >
                                            <FilterAltOutlined fontSize="small" />
                                        </IconButton>
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!isLoading && !loadError && filteredVersions.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {i18next.t("customerPtfVersions.noVersions")}
                                </TableCell>
                            </TableRow>
                        )}
                        {filteredVersions.map((version, index) => (
                            <TableRow
                                key={`${version.customer_number}-${version.product_full_name}-${version.environment_type}-${version.applied_version}-${index}`}
                            >
                                <TableCell>{version.customer_number}</TableCell>
                                <TableCell>{version.customer_name}</TableCell>
                                <TableCell>{version.product_full_name}</TableCell>
                                <TableCell>{version.product_code}</TableCell>
                                <TableCell>{version.environment_type}</TableCell>
                                <TableCell>{version.version_major}</TableCell>
                                <TableCell>{version.applied_version}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Popover
                open={Boolean(filterAnchor && activeFilterColumn)}
                anchorEl={filterAnchor}
                onClose={closeColumnFilter}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                {activeFilterColumn && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "12px" }}>
                        <TextField
                            autoFocus
                            value={columnFilters[activeFilterColumn]}
                            onChange={event => setColumnFilters(current => ({ ...current, [activeFilterColumn]: event.target.value }))}
                            placeholder={i18next.t("customerPtfVersions.headerFilter")}
                            size="small"
                            inputProps={{
                                "aria-label": i18next.t(customerPtfVersionColumns.find(column => column.key === activeFilterColumn)?.labelKey ?? "")
                            }}
                        />
                        {columnFilters[activeFilterColumn] && (
                            <IconButton
                                aria-label={i18next.t("customerPtfVersions.clearFilter")}
                                title={i18next.t("customerPtfVersions.clearFilter")}
                                onClick={() => setColumnFilters(current => ({ ...current, [activeFilterColumn]: "" }))}
                                size="small"
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        )}
                    </div>
                )}
            </Popover>
        </div>
    );
};

export default CustomerPtfVersions;
